import { Directive, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { LocaleService } from '@shared/services/locale.service';

@Directive({
	selector: '[appReadMore]',
})
export class ReadMoreDirective implements OnInit {
	@Input() description: string;
	@Input() descriptionUnmasking: boolean;

	@Output() phoneNumberClicked: EventEmitter<{ clicked: boolean; number: string }> = new EventEmitter<{ clicked: boolean; number: string }>();
	@ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

	constructor(private elementRef: ElementRef, private renderer: Renderer2, private locale: LocaleService) {}
	updatedDescription: any;
	allphoneNumbers: string[] = [];
	lines: any;
	showMoreButton: HTMLElement;
	showLessButton: HTMLElement;

	ngOnInit(): void {
		this.truncateText(this.description);
	}

	private truncateText(description: any): void {
		const ELEMENT = this.elementRef.nativeElement;
		this.lines = description.split('\n');
		let index = 0;

		this.updatedDescription = this.lines.map((line: string, lineIndex: number) => {
			const SPAN_CLASS_EXISTS = line.includes('unmasked');
			const MATCHES = line.match(/\d+/g);

			if (MATCHES && !SPAN_CLASS_EXISTS) {
				MATCHES.forEach((phoneNumber: string) => {
					let maxLength = 11; // Default max length for phone numbers

					if (phoneNumber.startsWith('964') && phoneNumber.length === 13) {
						maxLength = 13;
					}

					if (phoneNumber.startsWith('+964') && phoneNumber.length === 14) {
						maxLength = 14;
					}

					if (phoneNumber.length >= 10 && phoneNumber.length <= maxLength) {
						this.allphoneNumbers.push(phoneNumber);
						const PHONE_NUMBER_INDEXES: { [phoneNumber: string]: number } = {};
						if (!PHONE_NUMBER_INDEXES.hasOwnProperty(phoneNumber)) {
							PHONE_NUMBER_INDEXES[phoneNumber] = index;
							index++;
						}

						const MASKED_PHONE_NUMBER = phoneNumber.substring(0, phoneNumber.length - 4) + 'X'.repeat(4);
						const PHONE_WITH_CLASS = `<span class="phone-number cursor-pointer phone-number-${PHONE_NUMBER_INDEXES[phoneNumber]}" data-line-id="line-${lineIndex}">${MASKED_PHONE_NUMBER}</span>`;
						line = line.replace(phoneNumber, PHONE_WITH_CLASS);
					}
				});
			}
			return line;
		});

		if (this.updatedDescription.length > 6) {
			const TRUNCATED_TEXT = this.updatedDescription.slice(0, 6).join('\n');
			ELEMENT.innerHTML = TRUNCATED_TEXT;

			if (!this.showMoreButton) {
				this.showMoreButton = this.createShowMoreButton();
				const parentElement = ELEMENT.parentNode;
				if (parentElement) {
					this.renderer.appendChild(parentElement, this.showMoreButton);
				}
			}

			if (this.showLessButton) {
				const parentElement = this.showLessButton.parentNode;
				if (parentElement) {
					this.renderer.removeChild(parentElement, this.showLessButton);
					this.showLessButton = undefined;
				}
			}
		} else {
			ELEMENT.innerHTML = this.updatedDescription.join('\n');
		}

		// Make the phone numbers clickable
		const PHONE_NUMBERS = ELEMENT.querySelectorAll('.phone-number');
		PHONE_NUMBERS.forEach((phoneNumber: HTMLElement) => {
			this.renderer.listen(phoneNumber, 'click', () => this.onPhoneNumberClick(phoneNumber));
		});
	}

	private createShowMoreButton(): HTMLElement {
		const BUTTON = this.renderer.createElement('button');
		BUTTON.classList.add('show-more-btn');
		this.renderer.addClass(BUTTON, 'mat-button');
		this.renderer.addClass(BUTTON, 'purple-btn');
		BUTTON.innerText = this.locale.translations['help']['placead']?.['n567'];
		BUTTON.addEventListener('click', () => this.expandText(this.updatedDescription.join('\n')));
		return BUTTON;
	}

	private expandText(description: string): void {
		const ELEMENT = this.elementRef.nativeElement;
		ELEMENT.innerHTML = description;

		if (this.showMoreButton) {
			const PARENT_ELEMENT = this.showMoreButton.parentNode;
			if (PARENT_ELEMENT) {
				this.renderer.removeChild(PARENT_ELEMENT, this.showMoreButton);
				this.showMoreButton = undefined;
			}
		}

		const PARENT_ELEMENT = ELEMENT.parentNode;
		this.showLessButton = this.createShowLessButton();
		this.showLessButton.addEventListener('click', () => this.truncateText(this.updatedDescription.join('\n')));

		if (PARENT_ELEMENT) {
			this.renderer.appendChild(PARENT_ELEMENT, this.showLessButton);
		}

		const PHONE_NUMBERS = ELEMENT.querySelectorAll('.phone-number');

		PHONE_NUMBERS.forEach((phoneNumber: HTMLElement) => {
			this.renderer.listen(phoneNumber, 'click', () => this.onPhoneNumberClick(phoneNumber));
		});
	}

	private createShowLessButton(): HTMLElement {
		const BUTTON = this.renderer.createElement('button');
		BUTTON.classList.add('show-less-btn');
		this.renderer.addClass(BUTTON, 'mat-button');
		this.renderer.addClass(BUTTON, 'purple-btn');
		BUTTON.innerText = this.locale.translations['help']['placead']?.['n568'];
		BUTTON.addEventListener('click', () => this.truncateText(this.updatedDescription.join('\n')));
		return BUTTON;
	}

	private onPhoneNumberClick(phoneNumber: HTMLElement): void {
		const CLASS_INDEX = Array.from(phoneNumber.classList).find((className) => className.startsWith('phone-number-'));
		let originalPhoneNumber;

		if (CLASS_INDEX) {
			const INDEX = Number(CLASS_INDEX.split('-').pop());
			originalPhoneNumber = this.allphoneNumbers[INDEX];

			if (originalPhoneNumber && this.descriptionUnmasking) {
				const LINE_ID = phoneNumber.getAttribute('data-line-id');
				const LINE_INDEX = this.updatedDescription.findIndex((line) => line.includes(LINE_ID));
				const LINE = this.updatedDescription[LINE_INDEX];
				const UPDATED_LINE = LINE.replace(phoneNumber.outerHTML, `<span class="phone-number unmasked">${originalPhoneNumber}</span>`);
				this.updatedDescription[LINE_INDEX] = UPDATED_LINE;
				phoneNumber.classList.remove(CLASS_INDEX);
				phoneNumber.innerText = originalPhoneNumber;

				Array.from(phoneNumber.classList).forEach((className) => {
					if (className.startsWith('phone-number-') && className !== CLASS_INDEX) {
						phoneNumber.classList.remove(className);
					}
				});
			}
		}

		this.phoneNumberClicked.emit({ clicked: true, number: originalPhoneNumber });
	}
}
